import React, { useState } from 'react';
import { Button, Dialog } from '@ygtechnologies/rubus-ui';
import { LoadingIndicator, DateTimeDisplay } from '@ygtechnologies/bygr-frontend-component-library';
import { useAsyncState } from '@/utils/hooks';
import {
    getReceivedEmail,
    getReceivedEmailAttachmentUrl,
    getReceivedEmailContentUrl,
} from '@/services/communicore';
import { AttachmentList, DetailList, formatProject } from './shared';
import theme from './Communicore.module.scss';

interface ReceivedEmailDetailsModalProps {
    id: string;
    onClose(): void;
}

const ReceivedEmailDetailsModal: React.FC<ReceivedEmailDetailsModalProps> = ({ id, onClose }) => {
    const [email] = useAsyncState(() => getReceivedEmail(id), [id], null);
    const [contentType, setContentType] = useState<'html' | 'text' | null>(null);

    const activeContentType = contentType ?? (email?.hasHtml ? 'html' : 'text');
    const hasAnyContent = !!email && (email.hasHtml || email.hasText);

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <Dialog.Content contentWidth="md">
                <Dialog.Header>Received email</Dialog.Header>
                {!email ? (
                    <LoadingIndicator />
                ) : (
                    <div>
                        <DetailList
                            items={[
                                { label: 'Received', value: <DateTimeDisplay value={email.receivedAt} /> },
                                { label: 'From', value: email.from.join(', ') },
                                { label: 'To', value: email.to.join(', ') },
                                { label: 'Subject', value: email.subject },
                                { label: 'Project', value: formatProject(email.projectName, email.projectId) },
                                { label: 'Type', value: email.type },
                                { label: 'Status', value: email.status },
                                { label: 'Return path', value: email.returnPath },
                                { label: 'Id', value: email.id },
                                { label: 'AWS email id', value: email.awsEmailId },
                                { label: 'Message id', value: email.messageId },
                                { label: 'Source', value: email.source },
                                { label: 'Destination', value: email.destination.join(', ') },
                            ]}
                        />

                        <h4 className={theme.sectionHeader}>Content</h4>
                        {hasAnyContent ? (
                            <div>
                                {email.hasHtml && email.hasText && (
                                    <div className={theme.contentToggle}>
                                        <Button
                                            variant={activeContentType === 'html' ? 'secondary' : 'tertiary'}
                                            onClick={() => setContentType('html')}>
                                            HTML
                                        </Button>
                                        <Button
                                            variant={activeContentType === 'text' ? 'secondary' : 'tertiary'}
                                            onClick={() => setContentType('text')}>
                                            Text
                                        </Button>
                                    </div>
                                )}
                                <iframe
                                    title="Email content"
                                    sandbox=""
                                    className={theme.bodyFrame}
                                    src={getReceivedEmailContentUrl(email.id, activeContentType)}
                                />
                            </div>
                        ) : (
                            <p>No content stored.</p>
                        )}

                        <h4 className={theme.sectionHeader}>Attachments</h4>
                        <AttachmentList
                            attachments={email.attachments}
                            getUrl={(fileId) => getReceivedEmailAttachmentUrl(email.id, fileId)}
                        />
                    </div>
                )}
            </Dialog.Content>
        </Dialog>
    );
};

export default ReceivedEmailDetailsModal;
